import type { StanceTip } from "../bar/describe.ts";
import { codeLabel } from "../controls/layout.ts";
import { STANCE_KEY, type Stance } from "../fight/stance.ts";
import { say } from "../../words.ts";
import { pageKeyHints } from "../touch/key-hints.ts";

/**
 * The stance button's words (P2.2-T13, ADR-073's amendment "the stance: a weapon put away"), every one a draft for the
 * author: the button's accessible name, which stays the same as it toggles while `aria-pressed` says the state, and
 * its tooltip in each stance, shown through the HUD's tooltip (hover, focus, or a long press that pins it).
 */

/**
 * The key the button and its tooltip name before a player chooses another, as the bar's slots name theirs.
 */
export const STANCE_KEY_LABEL = codeLabel(STANCE_KEY);

/**
 * The stance button's tooltip in a stance: its name, the key, the type line, and what a click does, the last line
 * muted. A line's `{key}` is the key's label.
 *
 * @param stance The stance.
 * @param key The label of the key the stance is bound to (`controls/`); R unless given.
 * @param keyed Whether a key may be named (`touch/key-hints.ts`); without one the lines name the Stance and Attack buttons.
 * @returns The tooltip.
 * @example
 * stanceTip("sheathed").name;
 * // "Weapon sheathed"
 */
export function stanceTip(stance: Stance, key: string = STANCE_KEY_LABEL, keyed: boolean = pageKeyHints.getSnapshot()): StanceTip {
  const name = stance === "drawn" ? say("hud.stance.drawn.name") : say("hud.stance.sheathed.name");
  const drawn = keyed ? [say("hud.stance.drawn.first"), say("hud.stance.drawn.second", { key })] : [say("hud.stance.drawn.firstTouch"), say("hud.stance.drawn.secondTouch")];
  const sheathed = keyed ? [say("hud.stance.sheathed.first"), say("hud.stance.sheathed.second", { key })] : [say("hud.stance.sheathed.firstTouch"), say("hud.stance.sheathed.secondTouch")];
  const texts = stance === "drawn" ? drawn : [...sheathed, say("hud.stance.sheathed.third")];
  const last = texts.length - 1;
  return { kind: "stance", name, key, typeLine: say("hud.stance.typeLine"), lines: texts.map((text, index) => ({ text, tone: index === last ? "muted" : "plain" })) };
}
